// API Client for TaskMate

import type {
  User,
  Task,
  TaskFilters,
  CreateTaskRequest,
  CreateUserRequest,
  PaginatedResponse,
  Establishment,
  Stats,
  ManagerStats,
  EstablishmentStats,
} from './types';

const API_URL = import.meta.env.VITE_API_URL || 'https://taskmate-worker.mr-magis05.workers.dev/api';

class ApiClient {
  private getHeaders(): HeadersInit {
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
    };

    // Add chat ID from Telegram or localStorage
    const chatId = localStorage.getItem('chat_id');
    if (chatId) {
      headers['x-chat-id'] = chatId;
    }

    return headers;
  }

  private async handleResponse<T>(response: Response): Promise<T> {
    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Unknown error' }));
      throw new Error(error.error || 'Request failed');
    }
    return response.json();
  }

  // Users
  async getUsers(filters?: { role?: string; establishment_id?: number }): Promise<User[]> {
    const params = new URLSearchParams();
    if (filters?.role) params.set('role', filters.role);
    if (filters?.establishment_id) params.set('establishment_id', String(filters.establishment_id));

    const url = `${API_URL}/users${params.toString() ? '?' + params : ''}`;
    const response = await fetch(url, { headers: this.getHeaders() });
    return this.handleResponse<User[]>(response);
  }

  async getUser(userId: number): Promise<User> {
    const response = await fetch(`${API_URL}/users/${userId}`, {
      headers: this.getHeaders(),
    });
    return this.handleResponse<User>(response);
  }

  async createUser(data: CreateUserRequest): Promise<User> {
    const response = await fetch(`${API_URL}/users`, {
      method: 'POST',
      headers: this.getHeaders(),
      body: JSON.stringify(data),
    });
    return this.handleResponse<User>(response);
  }

  async deleteUser(userId: number): Promise<{ success: boolean }> {
    const response = await fetch(`${API_URL}/users/${userId}`, {
      method: 'DELETE',
      headers: this.getHeaders(),
    });
    return this.handleResponse<{ success: boolean }>(response);
  }

  async updateUser(userId: number, data: Partial<CreateUserRequest>): Promise<User> {
    const response = await fetch(`${API_URL}/users/${userId}`, {
      method: 'PUT',
      headers: this.getHeaders(),
      body: JSON.stringify(data),
    });
    return this.handleResponse<User>(response);
  }

  // Establishments
  async getEstablishments(): Promise<Establishment[]> {
    const response = await fetch(`${API_URL}/establishments`, {
      headers: this.getHeaders(),
    });
    return this.handleResponse<Establishment[]>(response);
  }

  async createEstablishment(data: { name: string; address?: string }): Promise<Establishment> {
    const response = await fetch(`${API_URL}/establishments`, {
      method: 'POST',
      headers: this.getHeaders(),
      body: JSON.stringify(data),
    });
    return this.handleResponse<Establishment>(response);
  }

  async deleteEstablishment(establishmentId: number): Promise<{ success: boolean }> {
    const response = await fetch(`${API_URL}/establishments/${establishmentId}`, {
      method: 'DELETE',
      headers: this.getHeaders(),
    });
    return this.handleResponse<{ success: boolean }>(response);
  }

  // Tasks
  async getTasks(filters?: TaskFilters): Promise<PaginatedResponse<Task>> {
    const params = new URLSearchParams();
    if (filters?.user_id) params.set('user_id', String(filters.user_id));
    if (filters?.manager_id) params.set('manager_id', String(filters.manager_id));
    if (filters?.status) params.set('status', filters.status);
    if (filters?.establishment_id) params.set('establishment_id', String(filters.establishment_id));
    if (filters?.from) params.set('from', filters.from);
    if (filters?.to) params.set('to', filters.to);
    if (filters?.overdue) params.set('overdue', 'true');
    if (filters?.page) params.set('page', String(filters.page));
    if (filters?.limit) params.set('limit', String(filters.limit));

    const url = `${API_URL}/tasks${params.toString() ? '?' + params : ''}`;
    const response = await fetch(url, { headers: this.getHeaders() });
    return this.handleResponse<PaginatedResponse<Task>>(response);
  }

  async getTask(taskId: number): Promise<Task> {
    const response = await fetch(`${API_URL}/tasks/${taskId}`, {
      headers: this.getHeaders(),
    });
    return this.handleResponse<Task>(response);
  }

  async createTask(data: CreateTaskRequest): Promise<{ created: number[]; count: number }> {
    const response = await fetch(`${API_URL}/tasks`, {
      method: 'POST',
      headers: this.getHeaders(),
      body: JSON.stringify(data),
    });
    return this.handleResponse<{ created: number[]; count: number }>(response);
  }

  async updateTaskStatus(
    taskId: number,
    to_status: string,
    comment?: string
  ): Promise<{ success: boolean }> {
    const response = await fetch(`${API_URL}/tasks/${taskId}/status`, {
      method: 'POST',
      headers: this.getHeaders(),
      body: JSON.stringify({ to_status, comment }),
    });
    return this.handleResponse<{ success: boolean }>(response);
  }

  async addTaskComment(taskId: number, text: string): Promise<{ id: number; text: string }> {
    const response = await fetch(`${API_URL}/tasks/${taskId}/comments`, {
      method: 'POST',
      headers: this.getHeaders(),
      body: JSON.stringify({ text }),
    });
    return this.handleResponse<{ id: number; text: string }>(response);
  }

  async deleteTask(taskId: number): Promise<{ success: boolean }> {
    const response = await fetch(`${API_URL}/tasks/${taskId}`, {
      method: 'DELETE',
      headers: this.getHeaders(),
    });
    return this.handleResponse<{ success: boolean }>(response);
  }

  async getOverdueTasks(filters?: { user_id?: number; manager_id?: number; establishment_id?: number }): Promise<Task[]> {
    const params = new URLSearchParams();
    if (filters?.user_id) params.set('user_id', String(filters.user_id));
    if (filters?.establishment_id) params.set('establishment_id', String(filters.establishment_id));

    const url = `${API_URL}/tasks/overdue${params.toString() ? '?' + params : ''}`;
    const response = await fetch(url, { headers: this.getHeaders() });
    return this.handleResponse<Task[]>(response);
  }

  // Stats
  async getEmployeeStats(userId: number, days?: number): Promise<Stats> {
    const params = days ? `?days=${days}` : '';
    const response = await fetch(`${API_URL}/stats/employee/${userId}${params}`, {
      headers: this.getHeaders(),
    });
    return this.handleResponse<Stats>(response);
  }

  async getManagerStats(managerId: number, days?: number): Promise<ManagerStats> {
    const params = days ? `?days=${days}` : '';
    const response = await fetch(`${API_URL}/stats/manager/${managerId}${params}`, {
      headers: this.getHeaders(),
    });
    return this.handleResponse<ManagerStats>(response);
  }

  async getEstablishmentStats(establishmentId: number, days?: number): Promise<EstablishmentStats> {
    const params = days ? `?days=${days}` : '';
    const response = await fetch(`${API_URL}/stats/establishment/${establishmentId}${params}`, {
      headers: this.getHeaders(),
    });
    return this.handleResponse<EstablishmentStats>(response);
  }

  // Telegram Cache
  async getTelegramCache(): Promise<{ chat_id: number; username: string | null; first_name: string | null }[]> {
    const response = await fetch(`${API_URL}/telegram/cache`, {
      headers: this.getHeaders(),
    });
    return this.handleResponse<{ chat_id: number; username: string | null; first_name: string | null }[]>(response);
  }
}

export const api = new ApiClient();
